import { z } from 'zod';

import { cuidLike, nonEmptyString } from '../zod-common';

import { CHAT_MESSAGE_MAX_LENGTH } from './chat';

export const PROJECT_STATUSES = [
  'ACTIVE',
  'COMPLETED',
  'DISPUTED',
  'CANCELLED',
  'REOPENED',
  'DRAFT_REOPEN',
] as const;
export type ProjectStatusValue = (typeof PROJECT_STATUSES)[number];

export const TASK_STATUSES = [
  'TODO',
  'IN_PROGRESS',
  'AWAITING_VALIDATION',
  'VALIDATED',
  'REFUSED',
  'CONTESTED',
  'BLOCKED',
] as const;
export type TaskStatusValue = (typeof TASK_STATUSES)[number];

export const TASK_SOURCES = ['REQUESTER_EXPECTATION', 'BIDDER_ADDITION'] as const;
export type TaskSourceValue = (typeof TASK_SOURCES)[number];

export const REOPEN_SCOPES = ['NEW_EXPECTATIONS', 'EXTEND_TIMELINE'] as const;
export type ReopenScopeValue = (typeof REOPEN_SCOPES)[number];

export const projectByIdSchema = z.object({
  projectId: cuidLike,
});
export type ProjectByIdInput = z.infer<typeof projectByIdSchema>;

export const PROJECT_MAX_ASSIGNED_PEOPLE = 50;
export const projectAssignedPeopleSchema = z.object({
  projectId: cuidLike,
  assignedPeople: z
    .number()
    .int('Nombre entier requis')
    .min(1, 'Au moins 1 personne')
    .max(PROJECT_MAX_ASSIGNED_PEOPLE, `Max ${String(PROJECT_MAX_ASSIGNED_PEOPLE)} personnes`),
});
export type ProjectAssignedPeopleInput = z.infer<typeof projectAssignedPeopleSchema>;

export const PROJECT_EARLY_CLOSE_MIN_REASON_LENGTH = 50;
export const projectCloseSchema = z.object({
  projectId: cuidLike,
  reason: z.string().trim().min(PROJECT_EARLY_CLOSE_MIN_REASON_LENGTH).max(2_000).optional(),
});
export type ProjectCloseInput = z.infer<typeof projectCloseSchema>;

export const REOPEN_NEW_NEED_MAX_LENGTH = 1_000;

export const projectReopenSchema = z.object({
  projectId: cuidLike,
  scope: z.enum(REOPEN_SCOPES),
  newNeed: z.string().trim().max(REOPEN_NEW_NEED_MAX_LENGTH).optional(),
});
export type ProjectReopenInput = z.infer<typeof projectReopenSchema>;

export const reportGhostBidderSchema = z.object({
  projectId: cuidLike,
  reason: nonEmptyString.min(10, 'Min 10 caractères').max(2_000),
});
export type ReportGhostBidderInput = z.infer<typeof reportGhostBidderSchema>;

export const taskListSchema = z.object({
  projectId: cuidLike,
});
export type TaskListInput = z.infer<typeof taskListSchema>;

export const taskByIdSchema = z.object({
  taskId: cuidLike,
});
export type TaskByIdInput = z.infer<typeof taskByIdSchema>;

export const TASK_REFUSAL_MIN_REASON_LENGTH = 20;

export const TASK_DELIVERABLE_NOTE_MAX_LENGTH = 1_000;

export const MAX_DELIVERABLE_TOTAL_BYTES = 25 * 1024 * 1024;
export const MAX_DELIVERABLE_FILES = 20;
export const MAX_DELIVERABLE_LINKS = 20;

const deliverableR2KeyLike = z
  .string()
  .trim()
  .min(1)
  .max(512)
  .regex(/^[A-Za-z0-9._\-/]+$/, 'Invalid R2 key')
  .refine((key) => !key.split('/').includes('..'), 'Invalid R2 key');

const deliverableContentType = z
  .string()
  .regex(/^[\w.+-]+\/[\w.+-]+$/, 'Content-Type invalide')
  .max(120);

const deliverableLink = z
  .url('URL invalide')
  .refine((value) => /^https?:\/\//i.test(value), 'URL invalide');

const deliverableFileSchema = z.object({
  key: deliverableR2KeyLike,
  name: nonEmptyString.max(200),
  sizeBytes: z.number().int().min(1).max(MAX_DELIVERABLE_TOTAL_BYTES),
  contentType: deliverableContentType,
});
export type TaskDeliverableFileInput = z.infer<typeof deliverableFileSchema>;

export const taskDeliverableSchema = z
  .object({
    links: z.array(deliverableLink).max(MAX_DELIVERABLE_LINKS).optional(),
    files: z.array(deliverableFileSchema).max(MAX_DELIVERABLE_FILES).optional(),
  })
  .refine(
    (d) => (d.files ?? []).reduce((sum, f) => sum + f.sizeBytes, 0) <= MAX_DELIVERABLE_TOTAL_BYTES,
    { message: 'DELIVERABLE_TOO_LARGE', path: ['files'] },
  );
export type TaskDeliverableInput = z.infer<typeof taskDeliverableSchema>;

export const taskUpdateStatusSchema = z.object({
  taskId: cuidLike,
  status: z.enum([
    'IN_PROGRESS',
    'AWAITING_VALIDATION',
    'VALIDATED',
    'REFUSED',
    'CONTESTED',
    'BLOCKED',
    'TODO',
  ]),
  comment: z.string().trim().max(2_000).optional(),
  deliverable: taskDeliverableSchema.optional(),
});
export type TaskUpdateStatusInput = z.infer<typeof taskUpdateStatusSchema>;

export const taskSignDeliverableUploadSchema = z.object({
  taskId: cuidLike,
  filename: nonEmptyString.max(200),
  contentType: deliverableContentType,
  sizeBytes: z
    .number()
    .int()
    .min(1, 'DELIVERABLE_FILE_EMPTY')
    .max(MAX_DELIVERABLE_TOTAL_BYTES, 'DELIVERABLE_FILE_TOO_LARGE'),
});
export type TaskSignDeliverableUploadInput = z.infer<typeof taskSignDeliverableUploadSchema>;

export const taskDeliverableDownloadSchema = z.object({
  taskId: cuidLike,
  key: deliverableR2KeyLike,
});
export type TaskDeliverableDownloadInput = z.infer<typeof taskDeliverableDownloadSchema>;

export const taskContestThreadSchema = z.object({
  taskId: cuidLike,
});
export type TaskContestThreadInput = z.infer<typeof taskContestThreadSchema>;

export const taskPostContestMessageSchema = z.object({
  taskId: cuidLike,
  content: nonEmptyString.max(CHAT_MESSAGE_MAX_LENGTH, 'Max 4000 caractères'),
});
export type TaskPostContestMessageInput = z.infer<typeof taskPostContestMessageSchema>;

export const calendarByProjectSchema = z.object({
  projectId: cuidLike,
});
export type CalendarByProjectInput = z.infer<typeof calendarByProjectSchema>;

const CALENDAR_HORIZON_PAST_MS = 366 * 24 * 60 * 60 * 1000;
const CALENDAR_HORIZON_FUTURE_MS = 5 * 366 * 24 * 60 * 60 * 1000;

const calendarHorizonDate = z.coerce
  .date()
  .refine((date) => Number.isFinite(date.getTime()), { message: 'INVALID_DATE' })
  .refine((date) => date.getTime() >= Date.now() - CALENDAR_HORIZON_PAST_MS, {
    message: 'DATE_TOO_FAR_IN_PAST',
  })
  .refine((date) => date.getTime() <= Date.now() + CALENDAR_HORIZON_FUTURE_MS, {
    message: 'DATE_TOO_FAR_IN_FUTURE',
  });

export const calendarUpdateEntrySchema = z
  .object({
    entryId: cuidLike,
    startDate: calendarHorizonDate.optional(),
    endDate: calendarHorizonDate.optional(),
    priority: z.number().int().min(0).max(10).optional(),
    dependencies: z.array(cuidLike).max(50).optional(),
  })
  .refine(
    (data) =>
      data.startDate !== undefined ||
      data.endDate !== undefined ||
      data.priority !== undefined ||
      data.dependencies !== undefined,
    { message: 'AT_LEAST_ONE_FIELD' },
  )
  .refine(
    (data) =>
      data.startDate === undefined ||
      data.endDate === undefined ||
      data.startDate.getTime() <= data.endDate.getTime(),
    { message: 'START_DATE_BEFORE_END_DATE', path: ['startDate'] },
  );
export type CalendarUpdateEntryInput = z.infer<typeof calendarUpdateEntrySchema>;

export const CALENDAR_VIEW_MODES = ['GANTT', 'SPRINT', 'AGILE'] as const;
export type CalendarViewMode = (typeof CALENDAR_VIEW_MODES)[number];

export const CALENDAR_MAX_TIME_OFF_RANGES = 30;
export const CALENDAR_MIN_SPRINT_DAYS = 1;
export const CALENDAR_MAX_SPRINT_DAYS = 60;
export const CALENDAR_MAX_TIME_OFF_SPAN_DAYS = 366;

export const calendarTimeOffSchema = z
  .object({
    label: nonEmptyString.max(120, 'Max 120 caractères'),
    startDate: calendarHorizonDate,
    endDate: calendarHorizonDate,
  })
  .refine((data) => data.startDate.getTime() <= data.endDate.getTime(), {
    message: 'START_DATE_BEFORE_END_DATE',
    path: ['startDate'],
  })
  .refine(
    (data) =>
      data.endDate.getTime() - data.startDate.getTime() <=
      CALENDAR_MAX_TIME_OFF_SPAN_DAYS * 24 * 60 * 60 * 1000,
    { message: 'TIME_OFF_SPAN_TOO_LONG', path: ['endDate'] },
  );
export type CalendarTimeOffInput = z.infer<typeof calendarTimeOffSchema>;

export const calendarPlanningSettingsSchema = z.object({
  projectId: cuidLike,
  prioritizeByDependencies: z.boolean(),
  hoursPerDay: z.number().min(1, 'Min 1 h').max(24, 'Max 24 h'),
  workingWeekdays: z
    .array(z.number().int().min(0).max(6))
    .min(1, 'Au moins un jour')
    .max(7)
    .transform((days) => [...new Set(days)].sort((a, b) => a - b)),
  sprintLengthDays: z
    .number()
    .int()
    .min(CALENDAR_MIN_SPRINT_DAYS)
    .max(CALENDAR_MAX_SPRINT_DAYS)
    .nullable(),
  timeOff: z.array(calendarTimeOffSchema).max(CALENDAR_MAX_TIME_OFF_RANGES),
});
export type CalendarPlanningSettingsInput = z.infer<typeof calendarPlanningSettingsSchema>;

export const calendarRecomputeSchema = z.object({
  projectId: cuidLike,
});
export type CalendarRecomputeInput = z.infer<typeof calendarRecomputeSchema>;
